import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';

export const ROUND_TRANSITION_DELAY_MS = 4000;

export default function RoundSummary({ summary, onBackToBoard, onContinue, breakMinutes = null, backToBoardLabel = null }) {
  const remaining = Number(summary?.rounds_remaining || 0);
  const shouldAutoContinue = remaining > 0 && !summary?.preview && summary?.task_type !== 'turkish_l1_tour';
  const isEnglish = summary?.locale === 'en';
  const isMemory = summary?.task_type === 'memory_matching';
  const isReflex = summary?.task_type === 'reflex_catch';
  const isDigitRecall = summary?.task_type === 'digit_recall';
  const isOddOneOut = summary?.task_type === 'odd_one_out';
  const isCargoSorting = summary?.task_type === 'cargo_sorting';
  const isPatternRecognition = summary?.task_type === 'pattern_recognition';
  const isWordBuild = summary?.task_type === 'word_build';
  const handwritingTheme = summary?.is_handwriting_preview === true || summary?.is_dictation === true;
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(ROUND_TRANSITION_DELAY_MS / 1000));
  const [isStarting, setIsStarting] = useState(false);
  const [transitionError, setTransitionError] = useState('');
  const hasStartedRef = useRef(false);
  const onContinueRef = useRef(onContinue);

  useEffect(() => {
    onContinueRef.current = onContinue;
  }, [onContinue]);

  const startNextRound = useCallback(async () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    setIsStarting(true);
    setTransitionError('');
    try {
      const started = await onContinueRef.current?.();
      if (started === false) {
        throw new Error(isEnglish ? 'The next activity could not be opened.' : 'Sıradaki etkinlik açılamadı.');
      }
    } catch (error) {
      hasStartedRef.current = false;
      setIsStarting(false);
      setTransitionError(error?.message || (isEnglish ? 'The next activity could not be opened.' : 'Sıradaki etkinlik açılamadı.'));
    }
  }, [isEnglish]);

  useEffect(() => {
    if (!shouldAutoContinue) return undefined;
    const intervalId = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(0, current - 1));
    }, 1000);
    const timeoutId = window.setTimeout(startNextRound, ROUND_TRANSITION_DELAY_MS);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [shouldAutoContinue, startNextRound]);

  const headline = summary?.finish_reason === 'timer'
    ? (isEnglish ? 'Time is up. Your progress was saved!' : 'Süre doldu, yaptıkların kaydedildi!')
    : shouldAutoContinue && summary?.task_type === 'multiple_choice'
    ? (isEnglish ? 'Great work!' : 'Harika çalıştın!')
    : summary?.next_prompt || (isEnglish ? 'Great work!' : 'Harika çalıştın!');

  return (
    <Box
      className="round-summary"
      sx={{
        boxSizing: 'border-box',
        minHeight: '100vh',
        bgcolor: handwritingTheme ? '#f7f0ff' : '#f0fdf4',
        p: { xs: 2, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 280px)': { p: 1 },
        '& .MuiButton-root': {
          minHeight: 48,
          minWidth: 48,
          maxWidth: '100%',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          '&:focus-visible': { outline: handwritingTheme ? '3px solid #6d45df' : '3px solid #166534', outlineOffset: '3px' },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflowWrap: 'anywhere',
          maxWidth: 720,
          borderRadius: 2,
          bgcolor: '#ffffff',
          border: handwritingTheme ? '2px solid #d8c2f2' : '2px solid #bbf7d0',
          p: { xs: 3, md: 5 },
          '@media (max-width: 280px)': { p: 1.5 },
          textAlign: 'center',
          boxShadow: handwritingTheme ? '0 14px 36px rgba(90, 44, 152, 0.16)' : '0 14px 36px rgba(22, 101, 52, 0.16)',
        }}
      >
        {summary?.task_type === 'turkish_l1_tour' && <Typography sx={{ fontWeight: 900, color: handwritingTheme ? '#65329b' : '#166534', mb: 1 }}>Türkçe Yolculuğu</Typography>}
        <Typography sx={{ fontSize: { xs: 54, md: 74 }, lineHeight: 1, mb: 1 }}>{handwritingTheme ? '✏️' : '🌟'}</Typography>
        <Typography className="round-summary-title" sx={{ fontWeight: 1000, color: handwritingTheme ? '#572798' : '#166534', fontSize: { xs: 28, md: 40 }, mb: 1 }}>
          {headline}
        </Typography>
        <Typography sx={{ fontWeight: 900, color: '#334155', fontSize: { xs: 20, md: 26 }, mb: shouldAutoContinue ? 2 : 3 }}>
          {summary?.is_dictation
            ? (summary?.games?.[0]?.practice_ended_early
              ? 'Bugün güzel çalıştın. Sonra yine birlikte deneyebiliriz.'
              : 'Dinlediklerini yazdın. Eline sağlık!')
            : summary?.is_handwriting_preview
            ? (summary?.games?.[0]?.practice_ended_early
              ? 'Bugün güzel çalıştın! Sözcükleri başka zaman yine birlikte yazabiliriz.'
              : 'Dinlediklerini yazdın. Eline sağlık!')
            : summary?.is_balloon_finale
            ? `${Number(summary?.games?.[0]?.total_assessments || 5)} hedefi de buldun!`
            : summary?.task_type === 'turkish_l1_tour'
              ? summary?.games?.length === 1 ? 'Bugünkü oyununu tamamladın.' : 'İki oyunu da tamamladın.'
            : summary?.task_type === 'sound_hunt'
            ? 'Üç resimde a sesini buldun.'
            : summary?.task_type === 'letter_hunt'
            ? 'Dört a harfini de buldun.'
            : summary?.task_type === 'sound_discovery'
            ? 'Nar, inek ve limondaki ortak sesi keşfettin.'
            : summary?.task_type === 'target_sound_hunt'
            ? 'Nohut, deniz ve balondaki ortak sesi buldun.'
            : isReflex
            ? `Skorun: ${Number(summary?.score || 0)}`
            : isWordBuild
            ? (isEnglish
              ? `You completed ${Number(summary?.completed_word_count || 0)} words.`
              : `${Number(summary?.completed_word_count || 0)} kelimeyi tamamladın.`)
            : isCargoSorting
            ? `${Number(summary?.correct_count || 0)} doğru · ${Number(summary?.missed_count || 0)} kaçırılan`
            : isPatternRecognition
            ? `${Number(summary?.correct_count || 0)} / ${Number(summary?.question_count || 5)} doğru`
            : isOddOneOut
            ? `Skorun: ${Number(summary?.score || 0)} · %${Number(summary?.accuracy_pct || 0)} doğruluk`
            : isMemory
            ? (summary?.finish_reason === 'timer' ? 'Eşleştirdiklerin kaydedildi.' : 'İki turu da tamamladın.')
            : isDigitRecall
            ? `${Number(summary?.exact_sequence_count || 0)} / ${Number(summary?.sequence_count || 3)} sıra tam doğru`
            : isEnglish
            ? `${summary?.correct_count || 0} / ${summary?.question_count || 5} correct`
            : `${summary?.correct_count || 0} / ${summary?.question_count || 5} doğru`}
        </Typography>
        {breakMinutes && (
          <Box sx={{ mb: 3, p: 2.5, borderRadius: 3, bgcolor: '#eef2ff', border: '2px solid #c7d2fe' }}>
            <Typography sx={{ color: '#3730a3', fontSize: { xs: 22, md: 28 }, fontWeight: 900 }}>
              🌙 Üç etkinlik tamam! Mola zamanı
            </Typography>
            <Typography sx={{ mt: 1, color: '#4338ca', fontSize: { xs: 17, md: 21 } }}>
              Kartın yaklaşık {breakMinutes} dakika sonra yeniden açılacak. Şimdi sıra arkadaşlarında!
            </Typography>
          </Box>
        )}
        {shouldAutoContinue ? (
          <Box sx={{ maxWidth: 440, mx: 'auto' }} aria-live="polite">
            <Typography sx={{ color: '#475569', fontWeight: 800, fontSize: { xs: 17, md: 19 }, mb: 1.5 }}>
              {transitionError
                ? transitionError
                : isStarting
                ? (isEnglish ? 'Opening the next activity…' : 'Sıradaki etkinlik açılıyor…')
                : isEnglish
                ? `The next activity starts automatically in ${secondsLeft} seconds.`
                : `Sıradaki etkinlik ${secondsLeft} saniye içinde otomatik başlayacak.`}
            </Typography>
            {!transitionError ? <LinearProgress color="success" sx={{ height: 10, borderRadius: 999 }} /> : null}
            {transitionError ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                <Button variant="contained" size="large" onClick={startNextRound} sx={{ borderRadius: 2, fontWeight: 900 }}>
                  {isEnglish ? 'Try again' : 'Tekrar dene'}
                </Button>
                <Button variant="outlined" size="large" onClick={onBackToBoard} sx={{ borderRadius: 2, fontWeight: 900 }}>
                  {isEnglish ? 'Back to students' : 'Öğrenci listesine dön'}
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {remaining > 0 && summary?.preview && (
              <Button variant="contained" size="large" onClick={onContinue} sx={{ borderRadius: 2, fontWeight: 900 }}>
                {isEnglish ? 'Another round' : 'Bir tur daha'}
              </Button>
            )}
            <Button variant="outlined" size="large" onClick={onBackToBoard} sx={{ borderRadius: 2, fontWeight: 900,
              ...(backToBoardLabel ? { minHeight: 44, minWidth: 44 } : {}),
              ...((summary?.is_dictation || backToBoardLabel) ? { color: '#572798', borderColor: '#572798',
                '&:hover': { bgcolor: '#f7f0ff', borderColor: '#572798' } } : {}) }}>
              {backToBoardLabel || (summary?.is_handwriting_preview ? 'İçerik Değerlendirmeye dön' : isEnglish ? 'Back to students' : 'Öğrenci listesine dön')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
